import React, { useState, useEffect, useMemo } from 'react';
import { MoreHorizontal, Download, Search, Settings, ChevronDown, ChevronUp, Filter, X } from 'lucide-react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { exportToCSV } from '../../utils/exportUtils';
import { AdminTableProps } from './AdminInterfaces';
import { apiClient } from '../../config/api';

// Import shared table utilities and components
import {
  TableColumn,
  paginateData,
  getTotalPages,
  TableLoadingState,
  TableErrorState,
  TableEmptyState,
  ColumnHeader
} from '../../utils/tableUtils';

// Generic status badge component
const StatusBadge: React.FC<{ status: string; type: string }> = ({ status, type }) => {
  const getStatusColor = (status: string, type: string) => {
    if (type === 'active') {
      return status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
    }
    if (type === 'volunteers') {
      return status === 'Needed' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800';
    }
    if (type === 'timing') {
      if (status === 'Upcoming') return 'bg-blue-100 text-blue-800';
      if (status === 'Ongoing') return 'bg-green-100 text-green-800';
      if (status === 'Past') return 'bg-gray-100 text-gray-800';
    }
    if (type === 'skillLevel') {
      if (status === 'Beginner-Friendly') return 'bg-blue-100 text-blue-800';
      if (status === 'Intermediate' ) return 'bg-yellow-100 text-yellow-800';
      if (status === 'Advanced' ) return 'bg-purple-100 text-purple-800';
    }
    if (type === 'audition') {
      return status === 'Required' ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status, type)}`}>
      {status}
    </span>
  );
};

// Pagination component
const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (rows: number) => void;
  totalItems: number;
  entityName: string;
}> = ({ currentPage, totalPages, onPageChange, rowsPerPage, onRowsPerPageChange, totalItems, entityName }) => {
  const startItem = (currentPage - 1) * rowsPerPage + 1;
  const endItem = Math.min(currentPage * rowsPerPage, totalItems);

  return (
    <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-200">
      <div className="flex items-center gap-4">
        <div className="text-sm text-gray-700">
          Showing {startItem} to {endItem} of {totalItems} {entityName.toLowerCase()}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700">Show:</label>
          <select
            value={rowsPerPage}
            onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Previous
        </button>

        {[...Array(totalPages)].map((_, i) => {
          const page = i + 1;
          if (totalPages <= 7 || page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2) {
            return (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`px-3 py-1 border rounded text-sm ${
                  currentPage === page
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            );
          } else if (page === 2 || page === totalPages - 1) {
            return <span key={page} className="px-2 text-gray-400">...</span>;
          }
          return null;
        })}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

// Filter dropdown component
const FilterDropdown: React.FC<{
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  icon?: React.ReactNode;
}> = ({ label, value, options, onChange, icon }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 border rounded-md text-sm ${
          value ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 hover:bg-gray-50'
        }`}
      >
        {icon}
        {label}
        {value && <span className="ml-1 font-medium">({value})</span>}
        <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="py-1">
            <button
              onClick={() => {
                onChange('');
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
            >
              All {label}
            </button>
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                  value === option.value ? 'bg-blue-50 text-blue-700' : ''
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Bulk action bar component
const BulkActionBar: React.FC<{
  selectedCount: number;
  onBulkDelete: () => void;
  onClearSelection: () => void;
  entityName: string;
}> = ({ selectedCount, onBulkDelete, onClearSelection, entityName }) => {
  if (selectedCount === 0) return null;

  return (
    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="text-sm text-blue-700">
            {selectedCount} {entityName.toLowerCase()}{selectedCount > 1 ? 's' : ''} selected
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onBulkDelete}
            className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
          >
            Delete Selected
          </button>
          <button
            onClick={onClearSelection}
            className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
          >
            Clear Selection
          </button>
        </div>
      </div>
    </div>
  );
};

// Generic admin table configuration interface
interface AdminTableConfig<T> {
  entityName: string;
  apiEndpoint: string;
  columns: TableColumn<T>[];
  searchFields: (keyof T)[];
  filterOptions: {
    [key: string]: { value: string; label: string }[];
  };
  exportFileName: string;
  defaultVisibleColumns: { [key: string]: boolean };
}

interface AdminTableComponentProps<T> extends AdminTableProps<T> {
  config: AdminTableConfig<T>;
}

/**
 * Generic reusable admin table component that enforces consistency
 * across all admin pages following the "cattle not pets" principle.
 */
function AdminTable<T extends { id: string }>({
  onView,
  onEdit,
  onDelete,
  onViewVolunteers,
  config,
  isLoading = false,
  refreshTrigger = 0
}: AdminTableComponentProps<T>) {
  // Debug log to verify onViewVolunteers prop
  console.log('AdminTable - onViewVolunteers prop:', !!onViewVolunteers);

  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMenuItem, setActionMenuItem] = useState<string | null>(null);

  // Table state management
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filters, setFilters] = useState<{ [key: string]: string }>(() =>
    Object.keys(config.filterOptions).reduce((acc, key) => {
      acc[key] = '';
      return acc;
    }, {} as { [key: string]: string })
  );
  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Column settings state
  const [visibleColumns, setVisibleColumns] = useLocalStorage<{ [key: string]: boolean }>(
    `admin${config.entityName}Table_columnSettings`,
    config.defaultVisibleColumns
  );
  const [showColumnSettings, setShowColumnSettings] = useState<boolean>(false);

  // Helper functions
  const updateFilter = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters(Object.keys(config.filterOptions).reduce((acc, key) => {
      acc[key] = '';
      return acc;
    }, {} as { [key: string]: string }));
    setCurrentPage(1);
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  // Load data
  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      const response = await apiClient.get(config.apiEndpoint);
      setItems(response);
    } catch (error) {
      console.error(`Failed to load ${config.entityName.toLowerCase()}s:`, error);

      // Provide specific error messages based on the error type
      let errorMessage = `Failed to load ${config.entityName.toLowerCase()}s`;

      if (error instanceof Error) {
        if (error.message.includes('Access denied: 403')) {
          errorMessage = `Access denied. You may not have permission to view ${config.entityName.toLowerCase()}s, or your session may have expired. Please try logging out and logging back in.`;
        } else if (error.message.includes('Authentication session expired')) {
          errorMessage = 'Your session has expired. Please log in again.';
        } else if (error.message.includes('403')) {
          errorMessage = `Insufficient permissions to access ${config.entityName.toLowerCase()}s. Please contact your administrator.`;
        } else {
          errorMessage = error.message;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Export functionality
  const handleExport = () => {
    const exportData = items.map(item => {
      const exportItem: any = {};
      config.columns.forEach(column => {
        if (visibleColumns[column.id] && column.id !== 'actions') {
          const value = typeof column.accessor === 'function'
            ? column.accessor(item)
            : item[column.accessor as keyof T];
          exportItem[column.label] = value;
        }
      });
      return exportItem;
    });
    exportToCSV(exportData, config.exportFileName);
  };

  // Bulk actions
  const handleBulkDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${selectedItems.length} ${config.entityName.toLowerCase()}(s)?`)) {
      selectedItems.forEach(item => onDelete(item.id));
      setSelectedItems([]);
    }
  };

  // Filter and sort data
  const processedData = useMemo(() => {
    let filtered = items;

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(item =>
        config.searchFields.some(field =>
          String(item[field]).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        filtered = filtered.filter(item => {
          const itemValue = item[key as keyof T];
          return String(itemValue).toLowerCase().includes(value.toLowerCase());
        });
      }
    });

    // Apply sorting
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortField as keyof T];
        const bValue = b[sortField as keyof T];

        // Convert to strings for comparison
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();

        if (sortDirection === 'asc') {
          return aStr.localeCompare(bStr);
        } else {
          return bStr.localeCompare(aStr);
        }
      });
    }

    return filtered;
  }, [items, searchTerm, filters, sortField, sortDirection, config.searchFields]);

  // Paginate data
  const paginatedData = useMemo(() => {
    return paginateData(processedData, currentPage, rowsPerPage);
  }, [processedData, currentPage, rowsPerPage]);

  const totalPages = getTotalPages(processedData.length, rowsPerPage);

  if (loading) return <TableLoadingState />;
  if (error) return <TableErrorState error={error} onRetry={loadData} />;
  if (items.length === 0) return <TableEmptyState entityName={config.entityName} />;

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Table Header with Search, Filters, and Actions */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder={`Search ${config.entityName.toLowerCase()}s...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(config.filterOptions).map(([key, options]) => (
              <FilterDropdown
                key={key}
                label={key.charAt(0).toUpperCase() + key.slice(1)}
                value={filters[key] || ''}
                options={options}
                onChange={(value) => updateFilter(key, value)}
                icon={<Filter size={16} />}
              />
            ))}

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                <X size={16} />
                Clear Filters
              </button>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
            >
              <Download size={16} />
              Export
            </button>

            <button
              onClick={() => setShowColumnSettings(!showColumnSettings)}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
            >
              <Settings size={16} />
              Columns
            </button>
          </div>
        </div>

        {/* Column Settings */}
        {showColumnSettings && (
          <div className="mt-4 p-3 bg-gray-50 rounded-md">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Visible Columns</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {config.columns.map((column) => (
                <label key={column.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={visibleColumns[column.id] || false}
                    onChange={(e) => setVisibleColumns({
                      ...visibleColumns,
                      [column.id]: e.target.checked
                    })}
                    className="rounded border-gray-300 focus:ring-blue-500"
                  />
                  {column.label}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedCount={selectedItems.length}
        onBulkDelete={handleBulkDelete}
        onClearSelection={() => setSelectedItems([])}
        entityName={config.entityName}
      />

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-center">
                <input
                  type="checkbox"
                  checked={selectedItems.length === paginatedData.length && paginatedData.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedItems(paginatedData);
                    } else {
                      setSelectedItems([]);
                    }
                  }}
                  className="rounded border-gray-300 focus:ring-blue-500"
                />
              </th>
              {config.columns.map((column) => (
                visibleColumns[column.id] && (
                  <th key={column.id} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <ColumnHeader
                      column={column}
                      sortField={sortField || ''}
                      sortDirection={sortDirection}
                      onSort={(field: string) => {
                        if (sortField === field) {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField(field);
                          setSortDirection('asc');
                        }
                      }}
                    />
                  </th>
                )
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData.map((item) => (
              <tr
                key={item.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onView(item)}
              >
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedItems.some(selected => selected.id === item.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (e.target.checked) {
                        setSelectedItems([...selectedItems, item]);
                      } else {
                        setSelectedItems(selectedItems.filter(selected => selected.id !== item.id));
                      }
                    }}
                    className="rounded border-gray-300 focus:ring-blue-500"
                  />
                </td>
                {config.columns.map((column) => (
                  visibleColumns[column.id] && (
                    <td key={column.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                      {column.id === 'actions' ? (
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setActionMenuItem(actionMenuItem === item.id ? null : item.id);
                            }}
                            className="p-1 rounded-full hover:bg-gray-100"
                            aria-label={`${config.entityName} actions`}
                          >
                            <MoreHorizontal size={18} />
                          </button>

                          {actionMenuItem === item.id && (
                            <div
                              className="absolute right-0 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <div className="py-1 flex flex-col" role="menu">
                                <button
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onView(item);
                                    setActionMenuItem(null);
                                  }}
                                >
                                  View
                                </button>
                                <button
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onEdit(item);
                                    setActionMenuItem(null);
                                  }}
                                >
                                  Edit
                                </button>
                                {onViewVolunteers && (
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-gray-100"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      onViewVolunteers(item);
                                      setActionMenuItem(null);
                                    }}
                                  >
                                    View Volunteers
                                  </button>
                                )}
                                <button
                                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onDelete(item.id);
                                    setActionMenuItem(null);
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          {typeof column.accessor === 'function'
                            ? column.accessor(item)
                            : String(item[column.accessor as keyof T])
                          }
                        </div>
                      )}
                    </td>
                  )
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={setRowsPerPage}
        totalItems={processedData.length}
        entityName={config.entityName}
      />
    </div>
  );
}

export default AdminTable;
export { StatusBadge };
